import React, { useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  answerBlockQuestion,
  answerBlock
} from 'actions';

import LocalisedMessage from 'components/Common/LocalisedMessage';
import QuestionnaireType from 'common/questionnaires/QuestionnaireType';
import QuestionBlockHeader from '../QuestionBlockHeader';
import Question from '../Question';

const QuestionBlock = ({ questionnaireType, block, messageValues, onAnswer }) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const formAnswers = useSelector(state => state?.questionnaires?.formQuestionnaire?.answers);
  const preQualAnswers = useSelector(state => state?.questionnaires?.preQualQuestionnaire?.answers);

  const isEmptyObject = (obj, depth = 0) => {
    const maxDepth = 5;
    if (depth > maxDepth) {
      return false;
    }
    if (!obj) {
      return true;
    }
    if (typeof obj !== 'object' && obj) {
      return false;
    }
    let isEmpty = true;
    const keys = Object.keys(obj);
    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i];
      if (obj[key]) {
        isEmpty = isEmptyObject(obj[key], depth + 1);
        if (isEmpty === false) {
          break;
        }
      }
    }
    return isEmpty;
  };

  const qAnswers = useMemo(() => {
    if (questionnaireType === QuestionnaireType.PreQualifying) {
      return preQualAnswers;
    }
    return formAnswers;
  }, [questionnaireType, preQualAnswers, formAnswers]);

  const [isLinkOpen, setIsLinkOpen] = useState((!isEmptyObject(block?.answers) || block?.questions?.some(x => x.answer)) ?? false);
  const [isCollapsed, setIsCollapsed] = useState(block?.isCollapsed ?? false);

  const isLinkBlock = () => {
    let isLink = block.isLinkBlock;
    if (block) {
      if (Object.prototype.hasOwnProperty.call(block, 'linkBlock')) {
        if (typeof block.linkBlock === 'boolean') {
          isLink = block.linkBlock;
        }
        if (typeof block.linkBlock === 'function') {
          isLink = block.linkBlock(block.answers, qAnswers, preQualAnswers);
        }
      }
    }

    if (!isLink && !isLinkOpen) {
      setIsLinkOpen(true);
    }

    return isLink;
  };

  const hasValidators = useMemo(() => {
    const hasConditions = block.questions?.some(x => x.validation?.conditions?.length > 0);
    let isOptional;
    if (block?.componentProps && Object.prototype.hasOwnProperty.call(block?.componentProps, 'optional')) {
      if (typeof block.componentProps.optional === 'boolean') {
        isOptional = !!block.componentProps.optional;
      }
      if (typeof block.componentProps.optional === 'function') {
        isOptional = !!block.componentProps.optional(block.answers, formAnswers, preQualAnswers);
      }
    }

    return hasConditions || isOptional !== true;
  }, [block, isLinkBlock()]);
  const canExpandCollapse = useMemo(() => block.expandCollapse === undefined || block.expandCollapse === null || block.expandCollapse === true, [block]);

  const hasDisabledFields = block.disabled === true || block?.questions?.some(x => x.disabled === true);

  const onQuestionChange = (q, value) => {
    dispatch(answerBlockQuestion(questionnaireType, block.id, q.id, value));
  };

  const onShowBlock = () => {
    setIsLinkOpen(true);
  };

  const onHideBlock = () => {
    if (!onAnswer || typeof onAnswer !== 'function') {
      dispatch(answerBlock(block.id, {}, undefined));
    } else {
      onAnswer(block, {}, undefined);
    }

    setIsLinkOpen(false);
    if (isLinkBlock()) {
      if (block.questions?.length > 0) {
        block.questions = block.questions.map((q) => {
          q.isValid = undefined;
          q.errorMessages = undefined;
          q.answer = undefined;
          return q;
        });
      }
    }
  };

  const showQuestion = (q) => {
    if (q) {
      if (Object.prototype.hasOwnProperty.call(q, 'visible')) {
        if (typeof q.visible === 'boolean') {
          return q.visible;
        }
        if (typeof q.visible === 'function') {
          return q.visible(block.answers, qAnswers, preQualAnswers);
        }
      }
    }
    return false;
  };

  const introExists = () => intl?.messages[`${ block.title }.intro`];

  useEffect(() => {
    if (isLinkBlock() && isEmptyObject(block.answers)) {
      setIsLinkOpen(false);
      block.isValid = undefined;
    }
  }, [isLinkBlock()]);

  return (
    <React.Fragment>
      { isLinkBlock() && !isLinkOpen && <button
        className="btn-block-link"
        type="button"
        onClick={ () => onShowBlock() }
        title={ `${ intl.formatMessage({ id: 'questionnaire.add' }) } ${ intl.formatMessage({ id: block.title }) }` }
        aria-label={ `${ intl.formatMessage({ id: 'questionnaire.add' }) } ${ intl.formatMessage({ id: block.title }) }` }
      >
        <FontAwesomeIcon icon="plus" className="fa-xs" /><LocalisedMessage id="questionnaire.add" /> { block.title && <LocalisedMessage id={ block.title } /> }{ hasValidators && ' *' }
      </button> }
      <div id={ block.id } className={ `question-card-wrapper ${ block.className ?? '' } ${ ((isLinkBlock() && !isLinkOpen) ? 'hide' : '') }` }>
        <fieldset id={ block.id } className="question-card">
          {!block.hideHeader
          && <QuestionBlockHeader
            id={ block.id }
            title={ block.title }
            info={ block.info }
            linkStyle={ isLinkBlock() ?? false }
            isCollapsed={ isCollapsed }
            expandCollapse={ block.expandCollapse }
            onExpandCollapseClick={ setIsCollapsed }
            onHideBlock={ () => onHideBlock() }
            messageValues={ messageValues }
          />}
          { introExists() && <div className="question-card-intro">
            <LocalisedMessage id={ `${ block.title }.intro` } />
          </div> }
          { hasDisabledFields && <p className="group-read-only"><LocalisedMessage id="preQualifyingQuestionnaire.readOnly.informationText" /></p> }
          <div className={ `questions row ${ canExpandCollapse && isCollapsed ? 'collapsed' : '' }` }>
            { block.component !== undefined && React.createElement(block.component, { block, preQualAnswers, messageValues, onAnswer }) }
            { block.questions !== undefined && block.questions.constructor === Array
              && block.questions.map(q => q && showQuestion(q) && <Question
                key={ q.id }
                blockId={ block.id }
                question={ q }
                { ...q.componentProps }
                messageValues={ messageValues }
                onChange={ (question, answer) => onQuestionChange(question, answer) }
              />)}
            { block.info?.footnote && <span className="col-12 sub-text"><LocalisedMessage id={ block.info.footnote } /></span> }
          </div>
        </fieldset>
      </div>
    </React.Fragment>);
};

QuestionBlock.propTypes = {
  questionnaireType: PropTypes.number.isRequired,
  block: PropTypes.shape().isRequired,
  formAnswers: PropTypes.shape(),
  preQualAnswers: PropTypes.shape(),
  messageValues: PropTypes.shape(),
  onAnswer: PropTypes.func
};

QuestionBlock.defaultProps = {
  formAnswers: null,
  preQualAnswers: null,
  messageValues: {},
  onAnswer: null
};

export default QuestionBlock;
